import { PERMISSIONS } from "@/constants/permissions"
import {
  ONBOARDING_DEMO_UPLOAD_FLAG,
  loadOnboardingDemoQuestionnaire,
} from "@/features/onboarding"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import { QuestionAnsweringTaskGroup } from "@/types/questionAnswering"
import { ApiCitation, ApiQuestionnaire } from "@/types/questionnaire/api"
import {
  EditResponsePairArgs,
  EditResponsePairRequestBody,
  Questionnaire,
  QuestionnaireResponsePair,
  QuestionnaireStatus,
  QuestionnaireStatusResponse,
  QuestionnaireUploadErrorHandler,
  QuestionnaireUploadSuccessHandler,
  UpdateAnswerPlacementArgs,
  UpdateQuestionnaireStatusArgs,
} from "@/types/questionnaire/models"
import { getFlagValue } from "@/utils/featureFlagStore"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { mapApiQuestionnaireToQuestionnaire } from "../adapters/questionnaireAdapter/questionnaireAdapter"
import apiClient, { apiClientV2 } from "./apiClient"
import {
  isQuestionnaireNotFound,
  useQuestionnaireRetry,
} from "./questionnaireQueryPolicy"
import { USER_LIMITS_QUERY_KEY } from "./userService"

export type AddManualFieldArgs = {
  questionnaireId?: string
  body: {
    question?: string | null
    type: "free-text" | "boolean"
    page: number
    x: number
    y: number
    width: number
    height: number
    font_size?: number
    options_placements?: Array<{
      text: string
      page: number
      x: number
      y: number
      width: number
      height: number
    }> | null
  }
}

export type DeleteResponsePairArgs = {
  questionnaireId?: string
  responsePairId: string
}

export type SignaturePlacementPayload = {
  signature_id: string
  page: number
  x: number
  y: number
  width: number
  height: number
}

export type UpsertSignaturePlacementArgs = {
  questionnaireId?: string
  placementId: string
  placement: SignaturePlacementPayload
}

export type DeleteSignaturePlacementArgs = {
  questionnaireId?: string
  placementId: string
}

export type QuestionnaireCategoryFilter = {
  categoryId: string
  tagIds: string[]
}

export type HydratedQuestionnaireFilterTag = {
  id: string
  displayNameEn: string
  displayNameDe?: string | null
}

export type HydratedQuestionnaireCategoryFilter = {
  categoryId: string
  categoryDisplayNameEn: string
  categoryDisplayNameDe?: string | null
  categoryColor?: string | null
  tagIds: string[]
  tags: HydratedQuestionnaireFilterTag[]
}

export type QuestionnaireFilterConfig = {
  categoryFilters: HydratedQuestionnaireCategoryFilter[]
  includeUnclassified: boolean
  excludeExpired: boolean
  matchedCount: number | null
}

export type SaveQuestionnaireFilterConfigArgs = {
  questionnaireId?: string
  body: {
    categoryFilters: QuestionnaireCategoryFilter[]
    includeUnclassified: boolean
    excludeExpired: boolean
  }
}

export type DeleteQuestionnaireFilterConfigArgs = {
  questionnaireId?: string
}

export type QuestionnaireAnsweringContext = {
  text: string
  terms: string[]
}

export type SaveQuestionnaireAnsweringContextArgs = {
  questionnaireId?: string
  body: QuestionnaireAnsweringContext
}

export type DeleteQuestionnaireAnsweringContextArgs = {
  questionnaireId?: string
}

type GetQuestionnaireDocumentOptions = {
  accept?: string
  demoVariant?: "original" | "answered"
}

type ExportFilledPdfResponse = {
  status: "ready" | string
  downloadUrl: string
  cached: boolean
  fileName: string
}

/**
 * The backend's Citation/Reference models require `content` and `location`
 * on every retrieved reference, and treat generatedResponsePart as a
 * {text,start,end} object (or null) — never the plain string ApiCitation
 * carries on the frontend. Sending that string through as-is would fail
 * Pydantic validation; drop it and fill in safe defaults for what's required.
 */
function toBackendSafeCitations(citations: ApiCitation[]): ApiCitation[] {
  return citations.map((citation) => ({
    generatedResponsePart: null,
    retrievedReferences: citation.retrievedReferences.map((ref) => ({
      ...ref,
      content: ref.content ?? "",
    })),
  }))
}

export const questionnaireApiCalls = {
  uploadQuestionnaire: async (file: File): Promise<Questionnaire> => {
    const formData = new FormData()
    formData.append("file", file)

    if (!getFlagValue("RAG_V2_QUESTIONNAIRES")) {
      const response = await apiClient.post<ApiQuestionnaire>(
        "/questionnaires",
        formData,
      )
      return mapApiQuestionnaireToQuestionnaire(response.data)
    }

    const response = await apiClientV2.post<{
      questionnaireId: string
      status: string
      message: string
      s3Key: string
    }>("/questionnaires", formData)

    return {
      id: response.data.questionnaireId,
      userId: "",
      userName: "",
      fileName: file.name,
      errorCode: null,
      errorMessage: "",
      isRetryable: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      organization: "",
      status: QuestionnaireStatus.Extracting,
      sections: [],
    }
  },
  getQuestionnaire: async (
    questionnaireId?: string,
    signal?: AbortSignal,
  ): Promise<Questionnaire> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    const response = await apiClient.get<ApiQuestionnaire>(
      `/questionnaires/${questionnaireId}`,
      { signal },
    )
    return mapApiQuestionnaireToQuestionnaire(response.data)
  },
  getQuestionnaires: async (): Promise<Questionnaire[]> => {
    const response = await apiClient.get<ApiQuestionnaire[]>("/questionnaires")
    const questionnaires = response.data.map((apiQuestionnaire) =>
      mapApiQuestionnaireToQuestionnaire(apiQuestionnaire),
    )
    return questionnaires
  },
  deleteQuestionnaire: async (questionnaireId?: string): Promise<void> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    await apiClient.delete<void>(`/questionnaires/${questionnaireId}`)
  },
  autofillQuestionnaire: async (
    questionnaireId: string,
    useCompanyProfile: boolean = true,
  ): Promise<QuestionAnsweringTaskGroup> => {
    const response = await apiClient.put<QuestionAnsweringTaskGroup>(
      `/questionnaires/${questionnaireId}/auto-fill`,
      { use_company_profile: useCompanyProfile },
    )
    return response.data
  },
  editResponsePair: async ({
    questionnaireId,
    responsePairId,
    question,
    response,
    citations,
  }: EditResponsePairArgs): Promise<Questionnaire> => {
    const requestBody: EditResponsePairRequestBody = {}
    if (question !== undefined) {
      requestBody.question = question
    }
    if (response !== undefined) {
      requestBody.response = response
    }
    if (citations !== undefined) {
      requestBody.citations = toBackendSafeCitations(citations)
    }

    const res = await apiClient.patch<ApiQuestionnaire>(
      `/questionnaires/${questionnaireId}/response-pair/${responsePairId}`,
      requestBody,
    )
    return mapApiQuestionnaireToQuestionnaire(res.data)
  },
  updateQuestionnaireStatus: async (
    data: UpdateQuestionnaireStatusArgs,
  ): Promise<Questionnaire> => {
    if (!data.questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    const response = await apiClient.patch<ApiQuestionnaire>(
      `/questionnaires/${data.questionnaireId}/status`,
      { status: data.status },
    )
    return mapApiQuestionnaireToQuestionnaire(response.data)
  },
  updateAnswerPlacement: async ({
    questionnaireId,
    responsePairId,
    placement,
  }: UpdateAnswerPlacementArgs): Promise<Questionnaire> => {
    // PUT /questionnaires/{questionnaire_id}/response-pairs/{response_pair_id}/placement
    return await apiClient.put(
      `/questionnaires/${questionnaireId}/response-pairs/${responsePairId}/placement`,
      placement,
    )
  },

  addManualField: async ({
    questionnaireId,
    body,
  }: AddManualFieldArgs): Promise<QuestionnaireResponsePair> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    const response = await apiClient.post<QuestionnaireResponsePair>(
      `/questionnaires/${questionnaireId}/response-pairs`,
      body,
    )

    return response.data
  },

  deleteResponsePair: async ({
    questionnaireId,
    responsePairId,
  }: DeleteResponsePairArgs): Promise<void> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    await apiClient.delete<void>(
      `/questionnaires/${questionnaireId}/response-pairs/${responsePairId}`,
    )
  },

  updateSignaturePlacement: async ({
    questionnaireId,
    placementId,
    placement,
  }: UpsertSignaturePlacementArgs): Promise<void> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    // PUT /questionnaires/{questionnaire_id}/signature-placements/{placement_id}
    await apiClient.put(
      `/questionnaires/${questionnaireId}/signature-placements/${placementId}`,
      placement,
    )
  },

  deleteSignaturePlacement: async ({
    questionnaireId,
    placementId,
  }: DeleteSignaturePlacementArgs): Promise<void> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    await apiClient.delete<void>(
      `/questionnaires/${questionnaireId}/signature-placements/${placementId}`,
    )
  },
  getQuestionnaireStatus: async (
    questionnaireId?: string,
    signal?: AbortSignal,
  ): Promise<QuestionnaireStatusResponse> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    const response = await apiClient.get<QuestionnaireStatusResponse>(
      `/questionnaires/${questionnaireId}/status`,
      { signal },
    )
    return response.data
  },
  getQuestionnaireDocument: async (
    questionnaireId?: string,
    options?: GetQuestionnaireDocumentOptions,
    signal?: AbortSignal,
  ): Promise<string> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }
    // Request raw binary blob to avoid JSON transformations
    const response = await apiClient.get<Blob>(
      `/questionnaires/${questionnaireId}/document`,
      {
        signal,
        headers: {
          accept:
            options?.accept ??
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
        params: options?.demoVariant
          ? { demoVariant: options.demoVariant }
          : undefined,
        responseType: "blob",
      },
    )

    const fileURL = URL.createObjectURL(response.data)
    return fileURL
  },
  getQuestionnaireSourceFile: async (
    questionnaireId?: string,
    signal?: AbortSignal,
  ): Promise<string> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }
    // Request raw binary blob to avoid JSON transformations
    const response = await apiClient.get<Blob>(
      `/questionnaires/${questionnaireId}/source-file`,
      { responseType: "blob", signal },
    )
    // Create object URL from blob for use with ExcelJSViewer
    const fileURL = URL.createObjectURL(response.data)
    return fileURL
  },
  getOriginalFile: async (
    questionnaireId?: string,
    signal?: AbortSignal,
  ): Promise<string> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }
    const response = await apiClient.get<Blob>(
      `/questionnaires/${questionnaireId}/original-file`,
      { responseType: "blob", signal },
    )
    return URL.createObjectURL(response.data)
  },

  exportFilledPdf: async (questionnaireId?: string): Promise<string> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }
    const response = await apiClient.get<ExportFilledPdfResponse>(
      `/questionnaires/${questionnaireId}/export-filled-pdf`,
    )

    return response.data.downloadUrl
  },
  getFilterConfig: async (
    questionnaireId?: string,
    signal?: AbortSignal,
  ): Promise<QuestionnaireFilterConfig> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    const response = await apiClientV2.get<QuestionnaireFilterConfig>(
      `/questionnaires/${questionnaireId}/filter-config`,
      { signal },
    )

    return response.data
  },
  saveFilterConfig: async ({
    questionnaireId,
    body,
  }: SaveQuestionnaireFilterConfigArgs): Promise<void> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    await apiClientV2.put(
      `/questionnaires/${questionnaireId}/filter-config`,
      body,
    )
  },
  deleteFilterConfig: async ({
    questionnaireId,
  }: DeleteQuestionnaireFilterConfigArgs): Promise<void> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    await apiClientV2.delete(`/questionnaires/${questionnaireId}/filter-config`)
  },
  getAnsweringContext: async (
    questionnaireId?: string,
  ): Promise<QuestionnaireAnsweringContext> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    const response = await apiClientV2.get<QuestionnaireAnsweringContext>(
      `/questionnaires/${questionnaireId}/answering-context`,
    )

    return response.data
  },
  saveAnsweringContext: async ({
    questionnaireId,
    body,
  }: SaveQuestionnaireAnsweringContextArgs): Promise<void> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    await apiClientV2.put(
      `/questionnaires/${questionnaireId}/answering-context`,
      body,
    )
  },
  deleteAnsweringContext: async ({
    questionnaireId,
  }: DeleteQuestionnaireAnsweringContextArgs): Promise<void> => {
    if (!questionnaireId) {
      throw new Error("Questionnaire Id is required")
    }

    await apiClientV2.delete(
      `/questionnaires/${questionnaireId}/answering-context`,
    )
  },
}

export const useUploadQuestionnaire = (
  handleSuccess: QuestionnaireUploadSuccessHandler,
  handleError: QuestionnaireUploadErrorHandler,
) => {
  const queryClient = useQueryClient()
  const mutation = useMutation<Questionnaire, Error, File>({
    mutationFn: async (file: File) => {
      const shouldUseDemoUpload =
        typeof window !== "undefined" &&
        sessionStorage.getItem(ONBOARDING_DEMO_UPLOAD_FLAG) === "true"

      if (shouldUseDemoUpload) {
        return loadOnboardingDemoQuestionnaire({ fileName: file.name })
      }

      return questionnaireApiCalls.uploadQuestionnaire(file)
    },
    onSuccess: handleSuccess,
    onError: handleError,
    onSettled: () => {
      if (typeof window !== "undefined") {
        sessionStorage.removeItem(ONBOARDING_DEMO_UPLOAD_FLAG)
      }
      // Keep the sidebar usage numbers fresh (also after a failed upload)
      void queryClient.invalidateQueries({ queryKey: USER_LIMITS_QUERY_KEY })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_CREATE)
}

type QuestionnaireQueryOptions = Parameters<typeof useQuery<Questionnaire>>[0]
type QuestionnaireRefetch = QuestionnaireQueryOptions["refetchOnWindowFocus"]

export const useGetQuestionnaireData = (
  questionnaireId?: string,
  options?: Omit<
    Parameters<typeof useQuery<Questionnaire>>[0],
    "queryKey" | "queryFn"
  >,
) => {
  const clientDefaults = useQueryClient().getDefaultOptions().queries
  const retry = useQuestionnaireRetry(options?.retry)
  // Only a missing questionnaire is forced to stay put. Otherwise the caller's choice wins, then the
  // QueryClient's, then the library's own (true), so this hook does not override the provider.
  // The cast is type-only: a client-wide callback is written for any query and is called with this one.
  const refetchOnWindowFocus: QuestionnaireRefetch =
    options?.refetchOnWindowFocus ??
    (clientDefaults?.refetchOnWindowFocus as QuestionnaireRefetch) ??
    true
  const refetchOnReconnect: QuestionnaireRefetch =
    options?.refetchOnReconnect ??
    (clientDefaults?.refetchOnReconnect as QuestionnaireRefetch) ??
    true
  return useQuery({
    queryKey: ["questionnaire", { questionnaireId }],
    queryFn: ({ signal }) =>
      questionnaireApiCalls.getQuestionnaire(questionnaireId, signal),
    ...options,
    enabled: !!questionnaireId && (options?.enabled ?? true),
    retry,
    refetchOnWindowFocus: (query) =>
      isQuestionnaireNotFound(query.state.error)
        ? false
        : typeof refetchOnWindowFocus === "function"
          ? refetchOnWindowFocus(query)
          : refetchOnWindowFocus,
    refetchOnReconnect: (query) =>
      isQuestionnaireNotFound(query.state.error)
        ? false
        : typeof refetchOnReconnect === "function"
          ? refetchOnReconnect(query)
          : refetchOnReconnect,
  })
}

export const useGetQuestionnairesData = () => {
  return useQuery({
    queryKey: ["questionnaires"],
    queryFn: () => questionnaireApiCalls.getQuestionnaires(),
  })
}

export const useGetQuestionnaireFilterConfig = (
  questionnaireId?: string,
  enabled: boolean = true,
) => {
  const retry = useQuestionnaireRetry()
  return useQuery({
    queryKey: ["questionnaireFilterConfig", { questionnaireId }],
    queryFn: ({ signal }) =>
      questionnaireApiCalls.getFilterConfig(questionnaireId, signal),
    enabled: !!questionnaireId && enabled,
    retry,
  })
}

export const useDeleteQuestionnaire = () => {
  const mutation = useMutation({
    mutationFn: (questionnaireId?: string) =>
      questionnaireApiCalls.deleteQuestionnaire(questionnaireId),
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_DELETE)
}

export type AutofillQuestionnaireArgs =
  | string
  | { questionnaireId: string; useCompanyProfile?: boolean }

export const useAutofillQuestionnaire = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    // A run spends credits: the sidebar and settings show them
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: USER_LIMITS_QUERY_KEY })
    },
    mutationFn: (args: AutofillQuestionnaireArgs) => {
      const questionnaireId =
        typeof args === "string" ? args : args.questionnaireId
      const useCompanyProfile =
        typeof args === "string" ? true : (args.useCompanyProfile ?? true)
      return questionnaireApiCalls.autofillQuestionnaire(
        questionnaireId,
        useCompanyProfile,
      )
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_RUN)
}

export const useEditResponsePair = () => {
  const mutation = useMutation<Questionnaire, Error, EditResponsePairArgs>({
    mutationFn: (data) => questionnaireApiCalls.editResponsePair(data),
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_RUN)
}

export const useUpdateQuestionnaireStatus = () => {
  const mutation = useMutation<
    Questionnaire,
    Error,
    UpdateQuestionnaireStatusArgs
  >({
    mutationFn: (data: UpdateQuestionnaireStatusArgs) =>
      questionnaireApiCalls.updateQuestionnaireStatus(data),
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_RUN)
}

export const useUpdateAnswerPlacement = () => {
  const mutation = useMutation<Questionnaire, Error, UpdateAnswerPlacementArgs>(
    {
      mutationFn: (data) => questionnaireApiCalls.updateAnswerPlacement(data),
    },
  )
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_RUN)
}

export const useAddManualField = () => {
  const mutation = useMutation<
    QuestionnaireResponsePair,
    Error,
    AddManualFieldArgs
  >({
    mutationFn: (data) => questionnaireApiCalls.addManualField(data),
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_RUN)
}

export const useDeleteResponsePair = () => {
  const mutation = useMutation<void, Error, DeleteResponsePairArgs>({
    mutationFn: (data) => questionnaireApiCalls.deleteResponsePair(data),
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_RUN)
}

export const useUpdateSignaturePlacement = () => {
  const mutation = useMutation<void, Error, UpsertSignaturePlacementArgs>({
    mutationFn: (data) => questionnaireApiCalls.updateSignaturePlacement(data),
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_RUN)
}

export const useDeleteSignaturePlacement = () => {
  const mutation = useMutation<void, Error, DeleteSignaturePlacementArgs>({
    mutationFn: (data) => questionnaireApiCalls.deleteSignaturePlacement(data),
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_RUN)
}

export const useSaveQuestionnaireFilterConfig = () => {
  const mutation = useMutation<void, Error, SaveQuestionnaireFilterConfigArgs>({
    mutationFn: (data) => questionnaireApiCalls.saveFilterConfig(data),
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_VIEW)
}

export const useDeleteQuestionnaireFilterConfig = () => {
  const mutation = useMutation<
    void,
    Error,
    DeleteQuestionnaireFilterConfigArgs
  >({
    mutationFn: (data) => questionnaireApiCalls.deleteFilterConfig(data),
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_VIEW)
}

export const ANSWERING_CONTEXT_QUERY_KEY = "questionnaireAnsweringContext"

export const useGetQuestionnaireAnsweringContext = (
  questionnaireId?: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [ANSWERING_CONTEXT_QUERY_KEY, { questionnaireId }],
    queryFn: () => questionnaireApiCalls.getAnsweringContext(questionnaireId),
    enabled: !!questionnaireId && enabled,
  })
}

export const useSaveQuestionnaireAnsweringContext = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation<
    void,
    Error,
    SaveQuestionnaireAnsweringContextArgs
  >({
    mutationFn: (data) => questionnaireApiCalls.saveAnsweringContext(data),
    onSuccess: (_result, { questionnaireId }) =>
      queryClient.invalidateQueries({
        queryKey: [ANSWERING_CONTEXT_QUERY_KEY, { questionnaireId }],
      }),
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_RUN)
}

export const useDeleteQuestionnaireAnsweringContext = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation<
    void,
    Error,
    DeleteQuestionnaireAnsweringContextArgs
  >({
    mutationFn: (data) => questionnaireApiCalls.deleteAnsweringContext(data),
    onSuccess: (_result, { questionnaireId }) =>
      queryClient.invalidateQueries({
        queryKey: [ANSWERING_CONTEXT_QUERY_KEY, { questionnaireId }],
      }),
  })
  return useGuardedMutation(mutation, PERMISSIONS.QUEST_RUN)
}

export const useGetQuestionnaireStatus = (
  questionnaireId?: string,
  enabled: boolean = true,
) => {
  const retry = useQuestionnaireRetry()
  return useQuery({
    queryKey: ["questionnaireStatus", { questionnaireId }],
    queryFn: ({ signal }) =>
      questionnaireApiCalls.getQuestionnaireStatus(questionnaireId, signal),
    enabled: !!questionnaireId && enabled,
    retry,
  })
}

export const useGetQuestionnaireDocument = (
  questionnaireId?: string,
  enabled: boolean = true,
  options?: GetQuestionnaireDocumentOptions,
) => {
  const queryOptions: GetQuestionnaireDocumentOptions | undefined = options
    ? {
        accept: options.accept,
        demoVariant: options.demoVariant,
      }
    : undefined

  const retry = useQuestionnaireRetry()
  return useQuery({
    queryKey: [
      "questionnaireDoc",
      {
        questionnaireId,
        options: queryOptions,
      },
    ],
    queryFn: ({ signal }) =>
      questionnaireApiCalls.getQuestionnaireDocument(
        questionnaireId,
        queryOptions,
        signal,
      ),
    enabled: !!questionnaireId && enabled,
    retry,

    staleTime: 5 * 60 * 1000,
    // Keep cached data around while component is mounted to prevent flashing
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: "always",
  })
}

export const useGetQuestionnaireSourceFile = (
  questionnaireId?: string,
  enabled: boolean = true,
) => {
  const retry = useQuestionnaireRetry()
  return useQuery({
    queryKey: ["questionnaireSourceFile", { questionnaireId }],
    queryFn: ({ signal }) =>
      questionnaireApiCalls.getQuestionnaireSourceFile(questionnaireId, signal),
    enabled: !!questionnaireId && enabled,
    retry,
  })
}

export const useGetOriginalFile = (
  questionnaireId?: string,
  enabled: boolean = true,
) => {
  const retry = useQuestionnaireRetry()
  return useQuery({
    queryKey: ["originalFile", { questionnaireId }],
    queryFn: ({ signal }) =>
      questionnaireApiCalls.getOriginalFile(questionnaireId, signal),
    enabled: !!questionnaireId && enabled,
    retry,
    staleTime: Infinity,
  })
}

export const useExportFilledPdf = (
  questionnaireId?: string,
  enabled: boolean = false,
) => {
  return useQuery({
    queryKey: ["exportFilledPdf", { questionnaireId }],
    queryFn: () => questionnaireApiCalls.exportFilledPdf(questionnaireId),
    enabled: !!questionnaireId && enabled,
    // Blob URLs are ephemeral - the browser can garbage-collect the underlying
    // data when the tab is backgrounded or after some time. Mark as immediately
    // stale so refetchOnWindowFocus triggers a fresh fetch.
    staleTime: 0,
    // Keep cached data around while component is mounted to prevent flashing
    gcTime: 5 * 60 * 1000, // 5 minutes
    // Refetch when the window regains focus (user returns to tab).
    refetchOnWindowFocus: true,
  })
}
